import React from "react";
import { Box, Grid, MenuItem, Skeleton, Stack, TextField } from "@mui/material";
import { isEmpty } from "lodash";
import { validateCedula } from "../../helpers/Helpers";
import { SeccionFormulario } from "../../components/base";

// Piezas que comparten el alta de cliente y la pestaña "Cliente" del detalle.

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
export const ERROR_OTRA_SESION = "Sesión activa en otro dispositivo";

// Sin tildes y sin mayúsculas, para que "Peña" se encuentre escribiendo "pena".
export const normalizar = (texto) =>
  String(texto ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

// Encabezado de tabla: texto en primary, en negrita y sin partirse en dos líneas.
export const encabezadoTabla = {
  fontWeight: 700,
  color: "primary.main",
  whiteSpace: "nowrap",
};

// Texto solo para lectores de pantalla.
export const ocultoVisual = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

// Respeta "reducir movimiento": el esqueleto queda quieto.
const sinAnimacion = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * Reglas de validación del cliente: las mismas de siempre y en el mismo orden que antes
 * (nombre, cédula vacía, cédula inválida, teléfono, sexo, estado civil, comunidad, dirección,
 * actividad y, solo en el alta, al menos una foto). Antes se mostraba la primera que fallaba;
 * ahora se devuelven todas juntas, cada una con la clave de su campo.
 *
 * @param {object} valores
 * @param {{ conFotos?: boolean, cantidadFotos?: number }} [opciones]
 * @returns {Object<string, string>} errores por campo; vacío si todo está bien.
 */
export const validarCliente = (valores, { conFotos = false, cantidadFotos = 0 } = {}) => {
  const errores = {};
  if (isEmpty(valores.firstName)) errores.firstName = "Ingrese el primer nombre";

  if (isEmpty(valores.identification)) {
    errores.identification = "Ingrese la cédula";
  } else if (!validateCedula(valores.identification)) {
    errores.identification = "La cédula no es válida. Formato: 000-000000-0000X";
  }

  if (isEmpty(valores.phone)) errores.phone = "Ingrese el teléfono";
  if (valores.sex === "") errores.sex = "Seleccione el sexo";
  if (valores.civilStatus === "") errores.civilStatus = "Seleccione el estado civil";
  if (valores.community === "") errores.community = "Seleccione la comunidad";
  if (isEmpty(valores.address)) errores.address = "Ingrese la dirección";
  if (isEmpty(valores.actividadEconomica))
    errores.actividadEconomica = "Ingrese la actividad económica";
  if (conFotos && cantidadFotos <= 0) errores.fotos = "Agregue al menos una foto";
  return errores;
};

// Lleva la vista y el foco al primer campo con error.
export const llevarAlPrimerError = (contenedor) => {
  setTimeout(() => {
    const primero = contenedor.current?.querySelector('[aria-invalid="true"], .Mui-error');
    if (primero) {
      if (typeof primero.focus === "function") primero.focus();
      if (typeof primero.scrollIntoView === "function")
        primero.scrollIntoView({ block: "center" });
    }
  }, 0);
};

// Nombre completo para mostrar (primer nombre, segundo, apellido, segundo), igual que lo arma el
// servidor pero sin espacios de más cuando falta alguno.
export const nombreCompleto = (v) =>
  `${v.firstName} ${v.secondName ?? ""} ${v.lastName} ${v.secondLastName ?? ""}`
    .replace(/\s+/g, " ")
    .trim();

const CampoEsqueleto = ({ md = 6 }) => (
  <Grid item xs={12} md={md}>
    <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2.5, ...sinAnimacion }} />
  </Grid>
);

/**
 * Esqueleto del formulario del cliente mientras llegan sus datos y las listas de ubicación.
 * Mismas secciones que el formulario, para que nada salte al cargar.
 *
 * @param {object} props
 * @param {boolean} [props.plana] Sin tarjetas (dentro de la pestaña del detalle).
 * @param {boolean} [props.conFotos] Incluye la sección de fotos.
 * @param {string} [props.etiqueta] Texto para lectores de pantalla (por defecto "Cargando…").
 */
export const CargandoFormularioCliente = ({
  plana = false,
  conFotos = true,
  etiqueta = "Cargando…",
}) => {
  const Contenedor = plana ? Box : Stack;
  return (
    <Box role="status" aria-live="polite">
      <Box component="span" sx={ocultoVisual}>
        {etiqueta}
      </Box>
      <Contenedor aria-hidden="true" {...(plana ? {} : { spacing: 3 })}>
        <SeccionFormulario titulo="Datos personales" plana={plana}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <CampoEsqueleto key={i} />
            ))}
          </Grid>
        </SeccionFormulario>
        <SeccionFormulario titulo="Ubicación" plana={plana}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[0, 1, 2].map((i) => (
              <CampoEsqueleto key={i} md={4} />
            ))}
            <CampoEsqueleto md={12} />
          </Grid>
        </SeccionFormulario>
        <SeccionFormulario titulo="Actividad económica" plana={plana}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <CampoEsqueleto />
            <CampoEsqueleto />
            <CampoEsqueleto md={12} />
          </Grid>
        </SeccionFormulario>
        {conFotos ? (
          <SeccionFormulario titulo="Fotos" plana={plana}>
            <Skeleton
              variant="rounded"
              height={40}
              width={160}
              sx={{ mt: 2, borderRadius: 2.5, ...sinAnimacion }}
            />
          </SeccionFormulario>
        ) : null}
      </Contenedor>
    </Box>
  );
};

// Un campo que depende de otro: mientras llega su lista se ve un esqueleto del tamaño del campo.
const CampoCargando = ({ etiqueta }) => (
  <Box role="status" aria-live="polite">
    <Box component="span" sx={ocultoVisual}>
      {etiqueta}
    </Box>
    <Skeleton
      aria-hidden="true"
      variant="rounded"
      height={56}
      sx={{ borderRadius: 2.5, ...sinAnimacion }}
    />
  </Box>
);

const vacioTexto = (texto) => <em>{texto}</em>;

/**
 * Las tres secciones de datos del cliente (Datos personales, Ubicación y Actividad económica)
 * con la validación junto a cada campo. Es solo presentación: el estado, las llamadas a la API
 * y el payload viven en quien lo usa.
 *
 * @param {object} props
 * @param {string} props.prefijo Para los ids de los campos (`useId()` de quien lo usa).
 * @param {object} props.valores firstName, secondName, lastName, secondLastName, identification, phone,
 *   sex, civilStatus, department, municipality, community, address, actividadEconomica, company, companyAddress.
 * @param {Object<string, string>} [props.errores] Texto de error por campo.
 * @param {(campo: string, valor: any) => void} props.onCambio
 * @param {Function} props.onDepartamento Evento del select de departamento (carga los municipios).
 * @param {Function} props.onMunicipio Evento del select de municipio (carga las comunidades).
 * @param {{departamentos: any[], municipios: any[], comunidades: any[]}} props.listas
 * @param {{municipios?: boolean, comunidades?: boolean}} [props.cargandoUbicacion]
 * @param {boolean} [props.soloLectura] Muestra los datos sin poder cambiarlos.
 * @param {boolean} [props.plana] Secciones sin tarjeta (dentro de la pestaña del detalle).
 */
export const FormularioCliente = ({
  prefijo,
  valores,
  errores = {},
  onCambio,
  onDepartamento,
  onMunicipio,
  listas,
  cargandoUbicacion = {},
  soloLectura = false,
  plana = false,
}) => {
  const texto = (campo, etiqueta, extra = {}) => (
    <TextField
      fullWidth
      id={`${prefijo}-${campo}`}
      label={etiqueta}
      value={valores[campo] ?? ""}
      onChange={(e) => onCambio(campo, e.target.value.toUpperCase())}
      error={Boolean(errores[campo])}
      helperText={errores[campo] || extra.ayuda || " "}
      InputProps={{ readOnly: soloLectura }}
      inputProps={{ autoComplete: "off", ...extra.inputProps }}
      {...extra.props}
    />
  );

  const opcionSelect = (campo, etiqueta, opciones, extra = {}) => (
    <TextField
      select
      fullWidth
      id={`${prefijo}-${campo}`}
      label={etiqueta}
      value={valores[campo]}
      onChange={extra.onChange ?? ((e) => onCambio(campo, e.target.value))}
      error={Boolean(errores[campo])}
      helperText={errores[campo] || extra.ayuda || " "}
      disabled={extra.disabled}
      SelectProps={{ readOnly: soloLectura }}
      {...extra.props}
    >
      {opciones}
    </TextField>
  );

  return (
    <>
      <SeccionFormulario titulo="Datos personales" plana={plana}>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            {texto("firstName", "Primer nombre", { props: { required: true } })}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("secondName", "Segundo nombre")}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("lastName", "Primer apellido")}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("secondLastName", "Segundo apellido")}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("identification", "Cédula", {
              ayuda: "Formato 000-000000-0000X",
              props: { required: true },
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("phone", "Teléfono", {
              inputProps: { inputMode: "tel" },
              props: { required: true },
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {opcionSelect(
              "sex",
              "Sexo",
              [
                <MenuItem key="m" value={0}>
                  MASCULINO
                </MenuItem>,
                <MenuItem key="f" value={1}>
                  FEMENINO
                </MenuItem>,
              ],
              { props: { required: true } }
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {opcionSelect(
              "civilStatus",
              "Estado civil",
              [
                <MenuItem key="s" value={0}>
                  {valores.sex === 0 ? "SOLTERO" : "SOLTERA"}
                </MenuItem>,
                <MenuItem key="c" value={1}>
                  {valores.sex === 0 ? "CASADO" : "CASADA"}
                </MenuItem>,
                <MenuItem key="u" value={2}>
                  UNION LIBRE
                </MenuItem>,
              ],
              { props: { required: true } }
            )}
          </Grid>
        </Grid>
      </SeccionFormulario>

      <SeccionFormulario titulo="Ubicación" plana={plana}>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            {opcionSelect(
              "department",
              "Departamento",
              [
                <MenuItem key="" value="">
                  {vacioTexto("Seleccione un departamento")}
                </MenuItem>,
                ...listas.departamentos.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                )),
              ],
              { onChange: onDepartamento, props: { required: true } }
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {cargandoUbicacion.municipios ? (
              <CampoCargando etiqueta="Cargando los municipios…" />
            ) : (
              opcionSelect(
                "municipality",
                "Municipio",
                [
                  <MenuItem key="" value="">
                    {vacioTexto("Seleccione un municipio")}
                  </MenuItem>,
                  ...listas.municipios.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  )),
                ],
                {
                  onChange: onMunicipio,
                  disabled: !soloLectura && listas.municipios.length === 0,
                  ayuda:
                    !soloLectura && listas.municipios.length === 0
                      ? "Elige primero el departamento"
                      : undefined,
                  props: { required: true },
                }
              )
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {cargandoUbicacion.comunidades ? (
              <CampoCargando etiqueta="Cargando las comunidades…" />
            ) : (
              opcionSelect(
                "community",
                "Comunidad",
                [
                  <MenuItem key="" value="">
                    {vacioTexto("Seleccione una comunidad")}
                  </MenuItem>,
                  ...listas.comunidades.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  )),
                ],
                {
                  disabled: !soloLectura && listas.comunidades.length === 0,
                  ayuda:
                    !soloLectura && listas.comunidades.length === 0
                      ? "Elige primero el municipio"
                      : undefined,
                  props: { required: true },
                }
              )
            )}
          </Grid>
          <Grid item xs={12}>
            {texto("address", "Dirección", {
              props: { required: true, multiline: true },
            })}
          </Grid>
        </Grid>
      </SeccionFormulario>

      <SeccionFormulario titulo="Actividad económica" plana={plana}>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            {texto("actividadEconomica", "Actividad económica", {
              props: { required: true },
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("company", "Centro de trabajo")}
          </Grid>
          <Grid item xs={12}>
            {texto("companyAddress", "Dirección del trabajo", {
              props: { multiline: true },
            })}
          </Grid>
        </Grid>
      </SeccionFormulario>
    </>
  );
};
